import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type {
  CurrentUserProfile,
  InviteUserInput,
  UpdateUserInput,
  UserProfile,
} from "@school-sales-ai/shared";
import { api } from "@/lib/api";
import { useAuthStore } from "@/stores/auth";
import { useClientSwitcherStore } from "@/stores/client-switcher.store";

export const CURRENT_USER_QUERY_KEY = ["me"] as const;

/**
 * The full query behind {@link useCurrentUserProfile}, for the one caller that
 * must tell "still loading" from "failed": the terms gate wraps every page, so
 * treating an error as loading would hang the whole CRM on a spinner.
 */
export function useCurrentUserProfileQuery() {
  const session = useAuthStore((s) => s.session);
  return useQuery({
    queryKey: CURRENT_USER_QUERY_KEY,
    queryFn: () => api.get<CurrentUserProfile>("/crm/users/me"),
    enabled: !!session,
  });
}

export function useCurrentUserProfile(): CurrentUserProfile | null {
  const { data } = useCurrentUserProfileQuery();
  return data ?? null;
}

export function useUsers() {
  const profile = useCurrentUserProfile();
  const activeClientId = useClientSwitcherStore((s) => s.activeClientId);
  // The same rule `useScopedClientId` applies everywhere else; it is spelled out
  // here rather than imported because this file owns `useCurrentUserProfile`,
  // which that hook reads — importing it back would close a cycle.
  // Account managers belong to the switcher side too: they own no client, so
  // keying on `profile.clientId` gave every school they manage the same (null)
  // cache entry.
  const usesClientSwitcher =
    profile?.role === "super_admin" || profile?.role === "account_manager";
  return useQuery({
    queryKey: ["users", usesClientSwitcher ? activeClientId : profile?.clientId ?? null],
    queryFn: () => api.get<UserProfile[]>("/crm/users"),
    // Client admins/members are scoped by their own clientId server-side.
    // Switcher roles must first pick a client (X-Client-Id header) or the backend 403s.
    enabled: usesClientSwitcher ? !!activeClientId : !!profile,
  });
}

export function useInviteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: InviteUserInput) =>
      api.post<UserProfile>("/crm/users", data),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success(`Invitación enviada a ${variables.email}.`);
    },
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: UpdateUserInput & { id: string }) =>
      api.patch<UserProfile>(`/crm/users/${id}`, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success("Cambios guardados.");
    },
  });
}

export function useDisableUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string; name: string }) =>
      api.post<null>(`/crm/users/${id}/disable`, {}),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success(`${variables.name} fue deshabilitado.`);
    },
  });
}

export function useEnableUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string; name: string }) =>
      api.post<null>(`/crm/users/${id}/enable`, {}),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success(`${variables.name} fue habilitado.`);
    },
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: ({ id }: { id: string; email: string }) =>
      api.post<null>(`/crm/users/${id}/reset-password`, {}),
    onSuccess: (_data, variables) => {
      toast.success(`Correo de reset enviado a ${variables.email}.`);
    },
  });
}

export function useResendInvite() {
  return useMutation({
    mutationFn: ({ id }: { id: string; email: string }) =>
      api.post<null>(`/crm/users/${id}/resend-invite`, {}),
    onSuccess: (_data, variables) => {
      toast.success(`Invitación reenviada a ${variables.email}.`);
    },
  });
}
